class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
		this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
		this.setHeaderCartIconAccessibility();
	}

	loadImages() {
		const images = this.querySelectorAll('img[lazy-src], img[lazy-srcset]');
		images.forEach((img) => {
			if (img.getAttribute('lazy-srcset')) {
				img.setAttribute('srcset', img.getAttribute('lazy-srcset'));
				img.removeAttribute('lazy-srcset');
			}
			if (img.getAttribute('lazy-src')) {
				img.setAttribute('src', img.getAttribute('lazy-src'));
				img.removeAttribute('lazy-src');
			}
		});
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector('#cart-icon-bubble');
		if (!cartLink) return;

		cartLink.setAttribute('role', 'button');
		cartLink.setAttribute('aria-haspopup', 'dialog');
		cartLink.addEventListener('click', (event) => {
			event.preventDefault();
			this.open(cartLink);
		});
		cartLink.addEventListener('keydown', (event) => {
			if (event.code.toUpperCase() === 'SPACE') {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add('animate', 'active');
		});

		this.addEventListener(
			'transitionend',
			() => {
				const containerToTrapFocusOn = this.classList.contains('is-empty')
					? this.querySelector('.drawer__inner-empty')
					: document.getElementById('CartDrawer');
				const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		document.body.classList.add('overflow-hidden');
	}

	close() {
		this.classList.remove('active');
		removeTrapFocus(this.activeElement);
		document.body.classList.remove('overflow-hidden');
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute('role', 'button');
		cartDrawerNote.setAttribute('aria-expanded', 'false');

		if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
			cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
		}

		cartDrawerNote.addEventListener('click', (event) => {
			event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
		});

		cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector('.drawer__inner').classList.contains('is-empty') &&
			this.querySelector('.drawer__inner').classList.remove('is-empty');
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement =
				document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

			if (!sectionElement) return;
			sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
		});

		setTimeout(() => {
			this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
			this.open();
		});
	}

	getSectionInnerHTML(html, selector = '.shopify-section') {
		return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{
				id: 'cart-drawer',
				section: this.dataset.sectionId,
				selector: '#CartDrawer',
			},
			{
				id: 'cart-icon-bubble',
				section: 'cart-icon-bubble',
				selector: '.shopify-section',
			},
		];
	}

	getSectionDOM(html, selector = '.shopify-section') {
		return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: 'CartDrawer',
				section: this.dataset.sectionId,
				selector: '.drawer__inner',
			},
			{
				id: 'cart-icon-bubble',
				section: 'cart-icon-bubble',
				selector: '.shopify-section',
			},
		];
	}
}

customElements.define('cart-drawer-items', CartDrawerItems);

class BaseAxSwiper extends HTMLElement {
	constructor() {
		super();
		this.initializeElements();
		this.config = JSON.parse(this.slider.dataset.options);
		this.swiperInstance = null;
	}

	initializeElements() {
		this.slider = this.querySelector('[id^="Swiper-"]');
		this.slides = this.querySelectorAll('.swiper-slide');
		this.prevButtonEl = this.querySelector('.swiper-button-prev');
		this.nextButtonEl = this.querySelector('.swiper-button-next');
		this.paginationEl = this.querySelector('.swiper-pagination');
	}

	initSwiper() {
		const interval = setInterval(() => {
			if (typeof window.Swiper === 'function') {
				clearInterval(interval);
				this.createSwiperInstance();
			}
		}, 100);
	}

	createSwiperInstance() {
		const commonConfig = {
			slidesPerView: this.config.slideInView,
			slidesPerGroup: this.config.slidesToMove,
			loop: this.config.loop,
			effect: this.config.effect,
			fadeEffect: { crossFade: true },
			grabCursor: this.config.grabCursor,
			navigation: {
				prevEl: this.prevButtonEl,
				nextEl: this.nextButtonEl,
			},
		};

		const breakpointsConfig = this.getBreakpointsConfig();

		this.swiperInstance = new window.Swiper(this.slider, {
			...commonConfig,
			breakpoints: breakpointsConfig,
		});
	}

	getBreakpointsConfig() {
		throw new Error('getBreakpointsConfig() must be implemented in subclass');
	}
}

class AxSwiperUpsell extends BaseAxSwiper {
	constructor() {
		super();
		this.nostoIsEnabled = this.dataset.enableNosto === 'true';

		if (this.nostoIsEnabled) {
			this.handleNosto();
		} else {
			this.initSwiper();
		}
	}

	async handleNosto() {
		this.nostoPlacement = this.dataset.nostoPlacement;
		this.nostoContent = this.querySelector('.swiper-wrapper');
		this.nostoTitleElement = this.querySelector('[data-nosto-title]');
		this.nostoResultId = null;

		try {
			// Fetch fresh cart data and update Nosto's tagging before we do anything else
			const cart = await (await fetch('/cart.js')).json();
			this.updateNostoCartTagging(cart);

			await this.waitForNosto(5000);
			this.initNosto();
		} catch (error) {
			console.error('[Nosto] Error loading:', error.message);
			if (this.parentElement) {
				this.parentElement.remove();
			}
		}
	}

	updateNostoCartTagging(cart) {
		// Remove any old nosto_cart divs
		document.querySelectorAll('.nosto_cart').forEach((e) => e.remove());

		if (!cart || cart.item_count === 0) {
			return;
		}

		// Create a new one with up-to-date data
		const cartDiv = document.createElement('div');
		cartDiv.className = 'nosto_cart';
		cartDiv.style.display = 'none';

		cart.items.forEach((item) => {
			const lineItemDiv = document.createElement('div');
			lineItemDiv.className = 'line_item';

			// Use the exact field names and format Nosto expects
			lineItemDiv.innerHTML = `
				<span class="product_id">${item.product_id}</span>
				<span class="sku_id">${item.variant_id}</span>
				<span class="quantity">${item.quantity}</span>
				<span class="name">${item.product_title.replace(/'/g, "\\'")}</span>
				<span class="unit_price">${(item.price / 100).toFixed(2)}</span>
				<span class="price_currency_code">${cart.currency}</span>
			`;
			cartDiv.appendChild(lineItemDiv);
		});

		document.body.appendChild(cartDiv);
	}

	initNosto() {
		const self = this;
		let skipEvents = window.location.href.includes('nosto=') ? { skipEvents: true } : {};

		nostojs((api) => {
			// Force Nosto to re-read the cart content
			api.resendCartTagging();

			// Add a small delay to allow Nosto's servers to process the cart update
			// before we request recommendations based on it.
			setTimeout(() => {
				api
					.createRecommendationRequest({ includeTagging: true })
					.setElements([self.nostoPlacement])
					.load(skipEvents)
					.then((response) => {
						if (!response.recommendations || Object.keys(response.recommendations).length === 0) {
							console.log('[Nosto] Recommendations object is empty. Hiding component.');
							this.removeSkeleton();
							if (this.parentElement) this.parentElement.remove();
							return;
						}

						const nostoResponse = response.recommendations[self.nostoPlacement];

						if (!nostoResponse) {
							this.removeSkeleton();
							if (this.parentElement) this.parentElement.remove();
							return;
						}

						self.nostoResultId = nostoResponse.result_id;
						const parser = new DOMParser();
						const doc = parser.parseFromString(nostoResponse.html || nostoResponse, 'text/html');
						const resultsNode = doc.querySelector('[data-nosto-recommendation]');

						if (resultsNode) {
							const results = JSON.parse(resultsNode.textContent);
							
							// Display dynamic title from Nosto
							if (results.title) {
								this.updateTitle(results.title);
							}
							
							this.constructProductCards(results);
						} else {
							console.error('[Nosto] Failed to find recommendation data in HTML response.');
							this.removeSkeleton();
							if (this.parentElement) this.parentElement.remove();
						}
					})
					.catch((error) => {
						console.error('[Nosto] Error loading recommendations:', error);
						this.removeSkeleton();
						if (this.parentElement) this.parentElement.remove();
					});
			}, 150);
		});
	}

	waitForNosto(timeout = 5000) {
		return new Promise((resolve, reject) => {
			let timeSpent = 0;
			const checkNosto = () => {
				if (typeof window.nostojs === 'function') {
					resolve();
				} else if (timeSpent >= timeout) {
					reject(new Error('Nosto load timeout'));
				} else {
					timeSpent += 100;
					setTimeout(checkNosto, 100);
				}
			};
			checkNosto();
		});
	}

	updateTitle(title) {
		if (!title || !this.nostoTitleElement) return;
		this.nostoTitleElement.textContent = title;
		this.nostoTitleElement.style.display = '';
	}

	constructProductCards(results) {
		if (!results || results.products.length === 0) {
			this.removeSkeleton();
			if (this.parentElement) this.parentElement.remove();
			return;
		}

		const productPromises = results.products.map(async (product) => {
			let urlStr = product.product_url;
			const queryIndex = urlStr.indexOf('?');
			if (queryIndex !== -1) {
				urlStr = urlStr.substring(0, queryIndex);
			}
			const handle = urlStr.substring(urlStr.lastIndexOf('/') + 1);
			const content = await this.loadContentFromAPI(handle);
			if (!content) return null;

			const tempElement = document.createElement('div');
			tempElement.innerHTML = content;
			const productItem = tempElement.querySelector('.product_upsell');
			if (!productItem) return null;

			productItem.href = this.addLanguageToUrl(product.product_url);
			productItem.dataset.productId = product.product_id;
			productItem.dataset.productUrl = product.product_url;

			const productItemContainer = document.createElement('div');
			productItemContainer.classList.add('swiper-slide', 'grid__item');
			productItemContainer.appendChild(productItem);

			return productItemContainer;
		});

		Promise.all(productPromises).then((productsHtml) => {
			this.removeSkeleton();
			this.nostoContent.innerHTML = '';
			productsHtml.filter(Boolean).forEach((element) => {
				this.nostoContent.appendChild(element);
			});
			this.setNostoTracking();
			this.initSwiper();
		});
	}

	async loadContentFromAPI(handle_product) {
		try {
			const response = await fetch(`${Shopify.routes.root}products/${handle_product}?view=cart-upsell-nosto`);
			if (!response.ok) throw new Error('Network response was not ok');
			return await response.text();
		} catch (error) {
			console.error('[Nosto] Error loading API data:', error);
			return null;
		}
	}

	addLanguageToUrl(url) {
		const lang = Shopify.routes.root.replace(/\//g, '');
		if (!lang || new RegExp(`/${lang}(/|$)`).test(url)) return url;
		return url.replace(/^(https?:\/\/[^/]+)(\/.*)$/, `$1/${lang}$2`);
	}

	removeSkeleton() {
		const skeleton = this.querySelector('.skeleton-container--spinner');
		if (skeleton) {
			skeleton.remove();
		}
	}

	setNostoTracking() {
		const clickElements = this.querySelectorAll('.product_upsell');
		clickElements.forEach((clickElement) => {
			clickElement.addEventListener('click', (event) => {
				event.preventDefault();
				const productId = clickElement.dataset.productId;
				const url = clickElement.dataset.productUrl;
				const isCtrl = event.ctrlKey || event.metaKey;

				nostojs((api) => {
					api
						.createRecommendationRequest()
						.setProducts([{ product_id: productId }], this.nostoResultId)
						.load()
						.then(() => {
							if (isCtrl) {
								window.open(url, '_blank');
							} else {
								window.location.href = url;
							}
						});
				});
			});
		});
	}

	getBreakpointsConfig() {
		return {
			0: {
				slidesPerView: this.config.slideInView,
				slidesPerGroup: this.config.slidesToMove,
				navigation: {
					prevEl: this.prevButtonEl,
					nextEl: this.nextButtonEl,
				},
				spaceBetween: 16,
				pagination: {
					el: this.paginationEl,
					clickable: true,
				},
			},
			750: {
				grabCursor: this.config.grabCursor,
				freeMode: false,
				spaceBetween: 16,
				slidesPerView: this.config.slideInView,
				slidesPerGroup: this.config.slidesToMove,
				navigation: {
					prevEl: this.prevButtonEl,
					nextEl: this.nextButtonEl,
				},
			},
		};
	}
}

customElements.get('swiper-upsell') || customElements.define('swiper-upsell', AxSwiperUpsell);

// Abonnement à l'événement cartUpdate via le système pub/sub
subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
	const cartDrawer = document.querySelector('cart-drawer');
	if (cartDrawer) {
		setTimeout(() => {
			cartDrawer.loadImages();
		}, 100);
	}
});
